/**
 * Dexie.js 数据库 Schema 定义
 */

import Dexie, { type Table } from 'dexie';
import type {
  CadCommand,
  EditorPreferences,
  ViewState,
} from '@puppycad/cad-core';

/**
 * 命令记录（数据库存储格式）
 */
export interface CommandRecord {
  autoId?: number;
  id: string;
  projectId: string;
  command: CadCommand;
  seqId: number;
}

/**
 * 视图状态记录
 */
export interface ViewStateRecord {
  projectId: string;
  state: ViewState;
}

/**
 * 网格缓存记录（可选）
 */
export interface MeshCacheRecord {
  id: string;
  projectId: string;
  meshData: ArrayBuffer;
  timestamp: number;
}

/**
 * 编辑器偏好记录（数据库存储格式）。
 * 主键直接用 EditorPreferences.projectId。
 */
export type EditorPreferencesRecord = EditorPreferences;

/**
 * PuppyCAD 数据库类
 */
export class PuppyCADDatabase extends Dexie {
  commands!: Table<CommandRecord, number>;
  viewStates!: Table<ViewStateRecord, string>;
  meshCache!: Table<MeshCacheRecord, string>;
  editorPreferences!: Table<EditorPreferencesRecord, string>;

  constructor() {
    super('PuppyCAD');

    // 版本 1 —— 历史 schema，包含 projects 表。保留定义让 Dexie 能从旧库升级。
    this.version(1).stores({
      projects: 'id, name, createdAt, updatedAt',
      commands: '++autoId, id, projectId, [projectId+seqId]',
      viewStates: 'projectId',
      meshCache: 'id, projectId, timestamp',
    });

    // 版本 2 —— 编辑器偏好重构：
    //   - 新增 editorPreferences 表（主键为后端 projectId）；
    //   - 彻底删除 projects 表（传 null 即为 drop）。老数据按产品决策直接丢弃，不迁移。
    this.version(2).stores({
      projects: null,
      editorPreferences: 'projectId, updatedAt',
    });

    // 版本 3：旧版曾把 CAD 级 undo/redo 历史表放在 web-client。
    // 这里保留声明只用于打开旧库并升级到版本 4，实际历史存储已经收口到 cad-core。
    this.version(3).stores({
      historyDocuments: 'documentId, updatedAt',
      historyTransactions: '[documentId+transactionId], documentId, [documentId+seq], [documentId+status], status',
      historyCheckpoints: '[documentId+checkpointId], documentId, [documentId+seq]',
      historyBlobs: 'blobId, hash, storageKey, storageTier, createdAt',
      historySchemaMigrations: 'id, appliedAt',
    });

    // 版本 4：删除 web-client 旧历史表。Undo/Redo 会话冷存储由 cad-core 的 browser adapter 管理。
    this.version(4).stores({
      historyDocuments: null,
      historyTransactions: null,
      historyCheckpoints: null,
      historyBlobs: null,
      historySchemaMigrations: null,
    });

    // 版本 5：旧版曾新增项目级装配体 Mate 文档表。
    // 该表仅作为版本 6 的删除来源保留，当前 Mate 不再做 web-client 本地持久化。
    this.version(5).stores({
      assemblyMateDocuments: 'projectId, updatedAt',
    });

    // 版本 6：删除 Mate 本地文档表。
    // Mate 关系必须来自当前装配体文档/cad-core 会话，不能从浏览器本地缓存恢复旧项目状态。
    this.version(6).stores({
      assemblyMateDocuments: null,
    });
  }
}

export const db = new PuppyCADDatabase();
export default db;
